import { useRef, useState } from "react";
import { useAtom, useAtomValue } from "jotai";
import { BootLog } from "./components/terminal/BootLog";
import { TabBar, type TabId } from "./components/terminal/TabBar";
import { Scanner } from "./features/scanner/Scanner";
import { Graduation } from "./features/graduation/Graduation";
import { Config } from "./features/config/Config";
import { Support } from "./features/support/Support";
import { chainAtom, feedStatusAtom, rpcThrottledAtom } from "./state/atoms";

const feedColor: Record<string, string> = {
  LIVE: "var(--flurry-green)",
  RECONNECTING: "var(--flurry-amber)",
  DEMO: "var(--flurry-amber)",
  STALE: "var(--flurry-amber)",
  DEGRADED: "var(--flurry-amber)",
  UNAVAILABLE: "var(--flurry-red)",
};

export function App() {
  const [booted, setBooted] = useState(false);
  const [tab, setTab] = useState<TabId>("scan");
  const [chain, setChain] = useAtom(chainAtom);
  const chainButtons = useRef<Record<typeof chain, HTMLButtonElement | null>>({
    avalanche: null,
    robinhood: null,
  });
  const feedStatus = useAtomValue(feedStatusAtom);
  const throttled = useAtomValue(rpcThrottledAtom);
  const selectedChain = chain === "avalanche" ? "AVALANCHE" : "ROBINHOOD";
  const hostedStatus = throttled ? "DEGRADED" : feedStatus;

  return (
    <div className="relative min-h-screen overflow-hidden">
      <div className="scanlines" />
      <div className="vignette" />
      <div className="crt relative z-10 mx-auto max-w-5xl px-4 py-5">
        <div className="mb-1 flex flex-wrap items-start justify-between gap-3">
          <h1
            className="text-4xl leading-none"
            style={{ textShadow: "0 0 12px #5bff8a88, 0 0 40px #5bff8a33" }}
          >
            FLURRY{" "}
            <span style={{ color: "var(--flurry-cyan)", textShadow: "0 0 14px #5be8ff88" }}>
              慌
            </span>{" "}
            TERMINAL
          </h1>
          <div className="flex flex-col items-end gap-2">
            <div
              className="inline-flex overflow-hidden"
              role="radiogroup"
              aria-label="Network"
              style={{
                border: "1px solid var(--flurry-cyan)",
                boxShadow: "0 0 16px #5be8ff22",
              }}
            >
              {(["avalanche", "robinhood"] as const).map((value) => {
                const selected = chain === value;
                const choose = (next: typeof value) => {
                  setChain(next);
                  requestAnimationFrame(() => chainButtons.current[next]?.focus());
                };
                return (
                  <button
                    key={value}
                    ref={(element) => {
                      chainButtons.current[value] = element;
                    }}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    tabIndex={selected ? 0 : -1}
                    onClick={() => setChain(value)}
                    onKeyDown={(event) => {
                      if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
                      event.preventDefault();
                      if (event.key === "Home") choose("avalanche");
                      else if (event.key === "End") choose("robinhood");
                      else choose(value === "avalanche" ? "robinhood" : "avalanche");
                    }}
                    className="min-h-11 min-w-16 px-4 text-xs font-bold tracking-[0.18em] focus-visible:outline-2"
                    style={{
                      color: selected ? "var(--flurry-bg)" : "var(--flurry-cyan)",
                      background: selected ? "var(--flurry-cyan)" : "var(--flurry-panel)",
                      border: "none",
                      borderLeft: value === "robinhood" ? "1px solid var(--flurry-cyan)" : "none",
                      cursor: "pointer",
                      textShadow: selected ? "none" : "0 0 8px #5be8ff66",
                    }}
                  >
                    {value === "avalanche" ? "AVAX" : "RH"}
                  </button>
                );
              })}
            </div>
            <div
              className="text-right text-xs"
              aria-live="polite"
              style={{ color: "var(--flurry-mid)" }}
            >
              CHAIN: <span style={{ color: "var(--flurry-cyan)" }}>{selectedChain}</span>
              {" // "}
              HOSTED FEED: <span style={{ color: feedColor[hostedStatus] }}>{hostedStatus}</span>
            </div>
          </div>
        </div>

        {!booted ? (
          <BootLog onDone={() => setBooted(true)} />
        ) : (
          <>
            <TabBar tab={tab} onTab={setTab} />
            {tab === "scan" && <Scanner />}
            {tab === "grad" && <Graduation />}
            {tab === "cfg" && <Config />}
            {tab === "don" && <Support />}
            <div className="mt-6 text-xs" style={{ color: "var(--flurry-dim)" }}>
              flurry · not financial advice, it&apos;s forensics
              <span className="cursor" style={{ color: "var(--flurry-green)" }}>
                █
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
